import { Check } from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  Typography,
  TypographyProps,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { Colors } from 'src/shared/components/styles';

type UncontrolledCheckBoxProps = {
  label: string;
  name?: string;
  labelPlacement: 'top' | 'bottom' | 'end' | 'start';
  size?: 'small' | 'medium';
  labelVariant?: TypographyProps['variant'];
  color?: string;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  italic?: boolean;
  readOnly?: boolean;
  useErrorSummery?: boolean;
  checked?: boolean;
  onChange: (checked: boolean) => void;
  error?: string;
};

export const UncontrolledCheckBox = ({
  label,
  name,
  labelPlacement,
  size = 'medium',
  labelVariant,
  color,
  sx,
  disabled = false,
  italic = false,
  readOnly = false,
  useErrorSummery = false,
  checked = false,
  onChange,
  error,
}: UncontrolledCheckBoxProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    onChange?.(newValue);
  };

  const isError = Boolean(error);

  return (
    <>
      <FormControlLabel
        sx={{
          fontStyle: italic ? 'italic' : 'normal',
          cursor: readOnly ? 'default' : '',
          color: color || undefined,
          '& .MuiFormControlLabel-label': {
            color: color || undefined,
          },
          ...sx,
        }}
        label={label}
        labelPlacement={labelPlacement}
        componentsProps={
          labelVariant ? { typography: { variant: labelVariant } } : undefined
        }
        control={
          <Checkbox
            name={name}
            disabled={disabled}
            checked={checked}
            size={size}
            onChange={!readOnly ? handleChange : undefined}
            readOnly={readOnly}
            inputProps={{ 'aria-label': 'uncontrolled' }}
            sx={{
              '& .MuiSvgIcon-root': {
                color: isError ? Colors.RED : color || 'primary',
              },
              pointerEvents: readOnly ? 'none' : 'auto',
            }}
            checkedIcon={readOnly ? <Check /> : undefined}
          />
        }
      />
      {isError && !useErrorSummery && (
        <Typography fontSize='12px' ml='14px' color='error'>
          {error}
        </Typography>
      )}
    </>
  );
};

UncontrolledCheckBox.defaultProps = {
  disabled: false,
  italic: false,
  readOnly: false,
  useErrorSummery: false,
  checked: false,
  error: undefined,
  name: undefined,
  size: 'medium',
  labelVariant: undefined,
  color: undefined,
  sx: undefined,
};
